import { api, apiUrl, tokens, type Clip } from "../lib/api.js";
import { SNIPPET_START, SNIPPET_END } from "@rp/shared";
import { formatBytes } from "../lib/uploader.js";
import { POSTLY_URL } from "../lib/publish.js";

/** Never put a game name into innerHTML without this -- it comes off a disk. */
const esc = (v: string) =>
  v.replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!,
  );

interface GameRow {
  game_id: string;
  game_name: string | null;
  clips: number;
  crowd_marked: number;
  sessions: number;
  total_ms: number;
  best_audience_score: number;
  avg_audience_score: number | null;
}

/**
 * The per-game strip above the clip grid.
 *
 * Only appears once there is something to compare — one game is a fact, not a
 * comparison, and a bar chart of a single value is decoration. The comparison
 * it supports is the only one the data can honestly carry: how the crowd
 * reacted to one game against another, from the streamer's own room, with no
 * model and no prediction.
 *
 * Clicking a game filters the grid to it, which is the question anyone looking
 * at these numbers asks next.
 */
function renderGames(games: GameRow[], active: string | null): string {
  if (games.length < 2) return "";

  const scored = games.filter((g) => g.avg_audience_score !== null);
  const topAvg = scored.length ? Math.max(...scored.map((g) => g.avg_audience_score!)) : 0;

  const cells = games.map((g) => {
    const name = g.game_name ?? g.game_id;
    const avg = g.avg_audience_score;
    // Relative to the best game rather than an absolute scale: "how does this
    // compare" is the question, and crowd sizes differ far too much between
    // streamers for an absolute bar to mean anything.
    const share = avg !== null && topAvg > 0 ? Math.round((avg / topAvg) * 100) : 0;
    const multiple =
      avg !== null && topAvg > 0 && avg < topAvg
        ? `${(topAvg / avg).toFixed(1)}× less than your best`
        : avg !== null && scored.length > 1 && avg === topAvg
          ? "your strongest game"
          : null;

    return `<button class="gm${g.game_id === active ? " on" : ""}" data-game="${esc(g.game_id)}"
              title="${esc(multiple ?? "No crowd reactions recorded for this game")}">
        <strong>${esc(name)}</strong>
        <span class="gm-bar"><i style="width:${share}%"></i></span>
        <span class="gm-meta">${g.clips} clip${g.clips === 1 ? "" : "s"}${
          avg !== null ? ` · 🔥 ${avg.toFixed(1)} avg` : " · no crowd data"
        }</span>
      </button>`;
  });

  return `<section class="games-strip">
      <h2>By game</h2>
      <p class="hint">Average audience reactions per clip, each game against your best.
         ${active ? `<button class="linkish" data-game="">Show all games</button>` : ""}</p>
      <div class="gm-row">${cells.join("")}</div>
    </section>`;
}

const hms = (ms: number) => {
  const t = Math.floor(ms / 1000);
  return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, "0")}`;
};

/**
 * A search snippet, with the matched words highlighted.
 *
 * Every piece is escaped and only then wrapped, so the markers decide where the
 * emphasis goes and the transcript never decides anything. The server sends two
 * control characters precisely so this step cannot be skipped by accident: a
 * raw snippet rendered without this shows them as nothing at all, which looks
 * fine and quietly loses the highlighting rather than breaking loudly.
 */
function snippetHtml(raw: string): string {
  return raw
    .split(SNIPPET_START)
    .map((chunk, i) => {
      if (i === 0) return esc(chunk);
      const cut = chunk.indexOf(SNIPPET_END);
      if (cut === -1) return esc(chunk);
      return `<mark>${esc(chunk.slice(0, cut))}</mark>${esc(chunk.slice(cut + 1))}`;
    })
    .join("");
}

export async function clipsView(root: HTMLElement) {
  root.innerHTML = `<header class="page-head"><h1>Clips</h1>
    <div class="head-actions">
      <input type="search" id="q" class="search" placeholder="Search what you said"
             autocomplete="off" spellcheck="false">
      <div class="seg" id="sort">
        <button data-sort="recent" class="on">Newest</button>
        <button data-sort="audience">Top reactions</button>
      </div>
      <a class="btn small" id="postly" href="${POSTLY_URL}" target="_blank" rel="noopener"
         title="Download your clips, then schedule and post them to every platform with Postly">
        Publish with Postly ↗
      </a>
    </div></header>
    <section id="games"></section>
    <div id="grid" class="loading">Loading…</div>`;
  const grid = root.querySelector<HTMLElement>("#grid")!;
  let sort: "recent" | "audience" = "recent";
  let gameFilter: string | null = null;
  let search = "";
  const gamesEl = root.querySelector<HTMLElement>("#games")!;

  // Delegated: the strip is re-rendered on every pass.
  gamesEl.addEventListener("click", (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLElement>("[data-game]");
    if (!btn) return;
    const id = btn.dataset.game ?? "";
    // Clicking the active game clears the filter, so the control is its own
    // undo rather than needing a separate one.
    gameFilter = !id || id === gameFilter ? null : id;
    void render();
  });

  // Debounced, because this fires on every keystroke and each one is a query
  // against a full-text index. 250ms is below the threshold where a search box
  // feels slow and well above the gap between two keys of the same word.
  const searchEl = root.querySelector<HTMLInputElement>("#q")!;
  let typing: ReturnType<typeof setTimeout> | null = null;
  searchEl.addEventListener("input", () => {
    if (typing) clearTimeout(typing);
    typing = setTimeout(() => {
      search = searchEl.value.trim();
      void render();
    }, 250);
  });

  root.querySelectorAll<HTMLButtonElement>("#sort button").forEach((b) => {
    b.addEventListener("click", () => {
      sort = (b.dataset.sort as "recent" | "audience") ?? "recent";
      root.querySelectorAll("#sort button").forEach((x) => x.classList.toggle("on", x === b));
      void render();
    });
  });

  async function render() {
    const params = new URLSearchParams();
    if (sort === "audience") params.set("sort", "audience");
    if (gameFilter) params.set("game_id", gameFilter);
    if (search) params.set("q", search);
    const q = params.toString() ? "?" + params.toString() : "";

    // In parallel, and the strip is allowed to fail on its own: a summary is
    // not worth losing the clip grid over.
    const [clipsRes, gamesRes] = await Promise.all([
      api<{ clips: Clip[]; searchable: number | null }>("GET", "/v1/clips" + q),
      api<{ games: GameRow[] }>("GET", "/v1/games").catch(() => ({ games: [] })),
    ]);
    const { clips, searchable } = clipsRes;
    gamesEl.innerHTML = renderGames(gamesRes.games, gameFilter);
    if (!clips.length && search) {
      grid.className = "empty";
      grid.innerHTML = `
        <div class="empty-icon" aria-hidden="true">🔍</div>
        <h2>Nothing said that</h2>
        <p>No clip matched <strong>${esc(search)}</strong>.</p>
        <p class="fineprint">Only clips rendered with captions on can be searched,
           because that is when the words get written down&nbsp;&mdash;
           ${searchable ?? 0} of your clips ${searchable === 1 ? "has" : "have"} them.
           Turn captions on before you render and the next ones join the search.</p>`;
      return;
    }

    if (!clips.length) {
      grid.className = "empty";
      grid.innerHTML = `
        <div class="empty-icon" aria-hidden="true">✂</div>
        <h2>No clips yet</h2>
        <p>Open a recording that's finished analysing, choose how you want them exported —
           vertical, captions, your logo — and generate. Every reaction becomes its own clip.</p>
        <div class="actions">
          <a class="btn primary" href="#/recordings">Go to recordings</a>
        </div>`;
      return;
    }

    grid.className = "clips";
    grid.innerHTML = clips
      .map(
        (c) => `<div class="clip s-${c.status}${c.suggested ? " is-suggested" : ""}">
          <div class="clip-body">
            <strong>${esc(c.title ?? c.video_title ?? c.game_name ?? `Reaction ${c.ordinal}`)}</strong>
            ${c.origin_author ? `<span class="attr">${esc(c.origin_author)}</span>` : ""}
            ${c.game_name && c.game_name !== (c.title ?? c.video_title)
              ? `<span class="attr">${esc(c.game_name)}</span>` : ""}
            ${c.snippet ? `<span class="snippet">${snippetHtml(c.snippet)}</span>` : ""}
            ${
              c.audience_score > 0
                ? `<span class="crowd" title="${c.audience_score} audience reactions">🔥 ${c.audience_score}</span>`
                : ""
            }
            ${
              // Where it came from, for a clip nobody marked by hand. In the
              // library the approval list is long gone, and a clip with no 🔥
              // and no explanation reads as one that came from nowhere.
              c.suggested_by === "voice"
                ? `<span class="crowd" title="Suggested because your mic went well above your normal here">🎙️</span>`
                : ""
            }
            <span class="meta">
              ${c.duration_ms ? hms(c.duration_ms) : "—"}
              ${c.size_bytes ? ` · ${formatBytes(Number(c.size_bytes))}` : ""}
            </span>
          </div>
          ${
            c.status === "ready"
              ? `<div class="clip-actions">
                   ${
                     c.captions_key
                       ? `<button class="link" data-cc="${c.id}" title="Download captions (.vtt)">CC</button>`
                       : ""
                   }
                   <button class="btn small" data-dl="${c.id}">Download</button>
                 </div>`
              : c.status === "failed"
                ? `<span class="status s-failed" title="${esc(c.failure_reason ?? "")}">Failed</span>`
                : c.source === "local"
                ? `<span class="status s-${c.status}"
                         title="Your recording is on your own PC, so Stream-Cut Studio cuts
 this clip. Open Studio on that machine and it goes out by itself.">In Studio</span>`
                : `<span class="status s-${c.status}">${
                      c.status === "rendering" ? "Rendering" : "Queued"
                    }</span>`
          }
        </div>`,
      )
      .join("");

    // Both the clip and its caption sidecar 302 to a signed URL; fetch with the
    // token, then follow the redirect, so the bytes never cross the app's own
    // connection and the auth header still reaches the endpoint.
    const pull = (url: string) =>
      fetch(apiUrl(url), { headers: { authorization: `Bearer ${tokens.access}` } })
        .then((r) => r.url && (location.href = r.url))
        .catch(() => {});

    grid.querySelectorAll<HTMLButtonElement>("[data-dl]").forEach((btn) => {
      btn.addEventListener("click", () => pull(`/v1/clips/${btn.dataset.dl}/download`));
    });
    grid.querySelectorAll<HTMLButtonElement>("[data-cc]").forEach((btn) => {
      btn.addEventListener("click", () => pull(`/v1/clips/${btn.dataset.cc}/captions`));
    });
  }

  await render();
  const poll = setInterval(() => {
    if (!document.body.contains(root)) return clearInterval(poll);
    void render();
  }, 4000);
}
